import React from 'react'
import { useHistory } from 'react-router-dom'

import { Formik } from 'formik'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Form, Col, Row, Divider, Button } from '_zari/core'

import CondicionesPagoConstant from 'constants/condicionesDePago'
import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import TiposDeDocumentoSunatConstant from 'constants/tiposDeDocumentoSunat'
import ListaDeMonedasConstant from 'constants/listaDeMonedas'

import { ZrSwal, RounderUtil, IdGen, DateHandler } from 'utils'

import * as InventarioService from 'services/inventario.service'

import { RetrocederButton } from 'sharedComponents/buttons'

import * as FormGroups from './formGroups'
import * as Modals from './modals'
import * as Sections from './sections'

import OrigenStrip from './components/origenStrip'

import { ValidationSchema } from './validation'

const actualizarFila = (current, { cantidad, precio, subTotal, flete, isc, __updatedField, ...restPayload }) => {
  current = {
    ...current,
    ...restPayload,
    cantidad,
    precio,
    subTotal,
    flete,
    isc
  }

  if (__updatedField === 'cantidad') {
    const numCantidad = parseFloat(cantidad || '0')
    const numPrecio = parseFloat(precio || '0')

    const nuevoTotal = RounderUtil.round(numCantidad * numPrecio)

    current.subTotal = nuevoTotal
    current.total = nuevoTotal
  }

  return current
}

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      let newState = state
        .map(val => ({
          ...val,
          __filaActiva: false,
          __focusCantidad: false,
          __focusAfectacion: false,
          __focusPrecio: false,
          __focusSubTotal: false,
          __focusIsc: false,
          __focusFlete: false,
          esFilaBusqueda: false
        }))
        .filter(x => x._idProductoPresentacion)

      let { nuevaFila, viaCodBar, ...payload } = action.payload

      return [
        ...newState,
        {
          ...payload,
          __filaActiva: !viaCodBar,
          __focusCantidad: !viaCodBar,
          _idFila: Math.round(new Date() / 1000),
          cantidad: '',
          precio: '',
          subTotal: '',
          total: '',
          flete: '',
          isc: ''
        },
        {
          __filaActiva: viaCodBar,
          esFilaBusqueda: true,
          _idFila: `${Math.round(new Date() / 1000)}-G`
        }
      ]
    }
    case 'actualizar': {
      const { saltarFila, ...nuevoPayload } = action.payload
      return state.map(val => {
        if (val.esFilaBusqueda) {
          val.__filaActiva = saltarFila
        } else {
          if (val._idFila === nuevoPayload._idFila) {
            val = actualizarFila(val, nuevoPayload)
          }
          val.__filaActiva = !saltarFila
        }
        return val
      })
    }
    case 'fill': {
      return action.payload
    }
    case 'actualizarUbicacion': {
      const { _idFila, ubicacion } = action.payload
      return state.map(val => {
        if (val._idFila === _idFila) {
          return { ...val, ...ubicacion }
        }
        return val
      })
    }
    case 'actualizarLotes': {
      const { _idFila, lotes } = action.payload
      return state.map(val => {
        if (val._idFila === _idFila) {
          return { ...val, lotes }
        }
        return val
      })
    }
    case 'actualizarUnidades': {
      const { _idFila, unidades } = action.payload
      return state.map(val => {
        if (val._idFila === _idFila) {
          return { ...val, unidades }
        }
        return val
      })
    }
    default:
      return state
  }
}

const NuevaMainForm = ({ editarData, codigoTransaccion }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const history = useHistory()

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, editarData.detalles, innerArr => {
    let output = []

    if (innerArr) {
      output = innerArr.map((x, k) => ({
        ...x,
        _idFila: k
      }))
    }

    output.push({
      esFilaBusqueda: true,
      _idFila: IdGen.newId()
    })

    return output
  })

  const mainFormikRef = React.useRef({})
  const idTemporalOrdenCompraRef = React.useRef('')

  const [mostrarListaOrdenesModal, setMostrarListaOrdenesModal] = React.useState(false)
  const [mostrarInfoOrdenModal, setMostrarInfoOrdenModal] = React.useState(false)

  const [filaUbicacion, setFilaUbicacion] = React.useState(null)
  const [filaRegistroLotes, setFilaRegistroLotes] = React.useState(null)
  const [filaRegistroActivosFijos, setFilaRegistroActivosFijos] = React.useState(null)
  const [origenIngreso, setOrigenIngreso] = React.useState('ORDEN_COMPRA')

  const abrirSeleccionUbicacion = React.useCallback((row) => {
    setFilaUbicacion(row)
  }, [])

  const abrirRegistroLotes = React.useCallback((row) => {
    setFilaRegistroLotes(row)
  }, [])

  const abrirRegistroActivosFijos = React.useCallback((row) => {
    setFilaRegistroActivosFijos(row)
  }, [])

  const onUbicacionElegida = React.useCallback((ubicacion) => {
    if (filaUbicacion) {
      detallesDispatcher({
        type: 'actualizarUbicacion',
        payload: { _idFila: filaUbicacion._idFila, ubicacion }
      })
    }
    setFilaUbicacion(null)
  }, [filaUbicacion])

  const onLotesGuardados = React.useCallback((lotes) => {
    if (filaRegistroLotes) {
      detallesDispatcher({
        type: 'actualizarLotes',
        payload: { _idFila: filaRegistroLotes._idFila, lotes }
      })
    }
    setFilaRegistroLotes(null)
  }, [filaRegistroLotes])

  const onUnidadesGuardadas = React.useCallback((unidades) => {
    if (filaRegistroActivosFijos) {
      detallesDispatcher({
        type: 'actualizarUnidades',
        payload: { _idFila: filaRegistroActivosFijos._idFila, unidades }
      })
    }
    setFilaRegistroActivosFijos(null)
  }, [filaRegistroActivosFijos])

  const formInitialValues = React.useMemo(
    () => ({
      _idModeloCuentas12: editarData._idModeloCuentas12 || '',
      _idOrdenCompra: editarData._idOrdenCompra ?? '',
      _idSocio: editarData._idSocio ?? '',
      codigoSocio: editarData.codigoSocio ?? '',
      codigoTransaccion: codigoTransaccion,
      comentario: editarData.comentario ?? '',
      condicionPago: editarData.condicionPago ?? CondicionesPagoConstant.CREDITO,
      documentoSocio: editarData.documentoSocio ?? '',
      fechaAlmacenamiento: editarData.fechaAlmacenamiento || DateHandler.format(new Date()),
      fechaEmision: editarData.fechaEmision ?? '',
      flagVerOrdenesCerradas: false,
      moneda: editarData.moneda ?? ListaDeMonedasConstant.SOLES,
      nombreSocio: editarData.nombreSocio ?? '',
      numeroDocumento: editarData.numeroDocumento ?? '',
      serieDocumento: editarData.serieDocumento ?? '',
      tipoCambio: editarData.tipoCambio ?? '',
      tipoDocumento: editarData.tipoDocumento ?? TiposDeDocumentoSunatConstant.GUIA_REMISION_REMITENTE,
      tipoDocumentoSocio: editarData.tipoDocumentoSocio ?? '',
      titulo: editarData.titulo ?? ''
    }),
    [codigoTransaccion, editarData]
  )

  const clickListarOrdenes = React.useCallback(e => {
    setMostrarListaOrdenesModal(true)
  }, [])

  const submitHandler = (data, { setSubmitting }) => {
    const filasReales = detalles.filter(
      (x) => x._idProductoPresentacion && parseFloat(x.cantidad || '0') !== 0
    )

    const TIPO_ACTIVO_FIJO = '3'

    // `tipo` viene del producto (BIEN='1', SERVICIO='2', ACTIVO_FIJO='3'). Antes
    // se comparaba contra `tipoExistencia`, que es otro campo (código SUNAT de
    // existencia) y no detectaba al activo fijo → el modal de registro de unidades
    // no se abría.
    for (const x of filasReales) {
      const cantRecibida = parseFloat(x.cantidad || '0')
      if (x.tipo === TIPO_ACTIVO_FIJO) {
        const esperadas = Math.round(cantRecibida)
        if (!Array.isArray(x.unidades) || x.unidades.length === 0) {
          __zariContext.snack(
            'error',
            `Registra las ${esperadas} unidades del activo fijo: ${x.nombreProducto || x.codigoProducto}`
          )
          if (setSubmitting) setSubmitting(false)
          return
        }
        if (x.unidades.length !== esperadas) {
          __zariContext.snack(
            'error',
            `Se esperaban ${esperadas} unidades del activo fijo ${x.nombreProducto || x.codigoProducto} pero hay ${x.unidades.length}.`
          )
          if (setSubmitting) setSubmitting(false)
          return
        }
        if (x.unidades.some(u => !u._idUbicacion)) {
          __zariContext.snack(
            'error',
            `Hay unidades del activo fijo ${x.nombreProducto || x.codigoProducto} sin ubicación asignada.`
          )
          if (setSubmitting) setSubmitting(false)
          return
        }
      } else if (x.flagLote) {
        if (!Array.isArray(x.lotes) || x.lotes.length === 0) {
          __zariContext.snack(
            'error',
            `Registra los lotes del producto: ${x.nombreProducto || x.codigoProducto}`
          )
          if (setSubmitting) setSubmitting(false)
          return
        }
        const sumaLotes = x.lotes.reduce((acc, l) => acc + parseFloat(l.cantidad || '0'), 0)
        if (Math.abs(sumaLotes - cantRecibida) > 0.0001) {
          __zariContext.snack(
            'error',
            `La suma de lotes (${sumaLotes}) no coincide con la cantidad recibida (${cantRecibida}) del producto: ${x.nombreProducto || x.codigoProducto}`
          )
          if (setSubmitting) setSubmitting(false)
          return
        }
      } else if (!x._idUbicacion) {
        __zariContext.snack(
          'error',
          `Falta asignar la ubicación física al producto: ${x.nombreProducto || x.codigoProducto}`
        )
        if (setSubmitting) setSubmitting(false)
        return
      }
    }

    const detallesNormalizados = filasReales.map((x) => {
      const esActivoFijo = x.tipo === TIPO_ACTIVO_FIJO
      const idAlmacen =
        x._idAlmacen ||
        (esActivoFijo && x.unidades && x.unidades[0]?._idAlmacen) ||
        (x.lotes && x.lotes[0]?._idAlmacen) ||
        null
      const idUbicacion =
        x._idUbicacion ||
        (esActivoFijo && x.unidades && x.unidades[0]?._idUbicacion) ||
        (x.lotes && x.lotes[0]?._idUbicacion) ||
        null
      return {
        _idProductoPresentacion: x._idProductoPresentacion,
        _idDetalleOrden: x._idDetalleOrden,
        afectacion: x.afectacion,
        cantidad: parseFloat(x.cantidad || '0'),
        codigoProducto: x.codigoProducto,
        nombreProducto: x.nombreProducto,
        precio: parseFloat(x.precio || '0'),
        subTotal: parseFloat(x.subTotal || '0'),
        total: parseFloat(x.total || '0'),
        _idAlmacen: idAlmacen,
        _idUbicacion: idUbicacion,
        flagLote: !!x.flagLote,
        tipo: x.tipo,
        tipoExistencia: x.tipoExistencia,
        codigoBarra: x.codigoBarra,
        lotes:
          x.flagLote && !esActivoFijo
            ? (x.lotes || []).map(l => ({
                numeroLote: l.numeroLote,
                fechaFabricacion: l.fechaFabricacion || null,
                fechaVencimiento: l.fechaVencimiento || null,
                cantidad: parseFloat(l.cantidad || '0'),
                _idAlmacen: l._idAlmacen,
                _idUbicacion: l._idUbicacion
              }))
            : undefined,
        unidades: esActivoFijo
          ? (x.unidades || []).map(u => ({
              numeroSerieProveedor: u.numeroSerieProveedor || null,
              _idAlmacen: u._idAlmacen,
              _idUbicacion: u._idUbicacion
            }))
          : undefined
      }
    })

    // Cuando el tipo de documento es "00 - OTROS/TICKET" la serie no se solicita en
    // el formulario; usamos "ALM" como serie por defecto, consistente con el flujo
    // de Inventarios Entrada/Salida y el default del kardex.
    const serieFinal = data.tipoDocumento === '00'
      ? 'ALM'
      : (data.serieDocumento || '').toUpperCase()

    const submitData = {
      ...data,
      numeroDocumento: parseFloat(data.numeroDocumento),
      serieDocumento: serieFinal,
      detalles: detallesNormalizados
    }

    InventarioService.nuevoIngresoAlmacenPorOrdenCompra(submitData)
      .then(({ data: data2 }) => {
        ZrSwal.showAlert({
          tipo: 'success',
          mensaje: (
            <>
              <span>Operaciones Generadas :</span> <br />
              {data2.operacionesGeneradas.map((x, k) => (
                <React.Fragment key={k}>
                  <span>
                    {k + 1}. {x}
                  </span>{' '}
                  <br />
                </React.Fragment>
              ))}
            </>
          ),
          onSuccess: () => {
            history.push(`/${CodigosTransaccionesConstant.INGRESOALMACENPORODENCOMPRA_LISTA}`)
          }
        })
      })
      .catch(({ data: { error } }) => {
        __zariContext.snack('error', error)
        if (setSubmitting) {
          setSubmitting(false)
        }
      })
  }

  return (
    <>
      <Row>
        <Col md={12}>
          <Formik
            initialValues={formInitialValues}
            onSubmit={submitHandler}
            innerRef={mainFormikRef}
            validationSchema={ValidationSchema}
            validateOnMount={false}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ errors, isSubmitting, setValues, touched, values, handleChange, handleSubmit, setFieldValue }) => (
              <>
                <Form>
                  <OrigenStrip origen={origenIngreso} onChange={setOrigenIngreso} />
                  <Row>
                    <Col md={4}>
                      <FormGroups.SocioFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors._idSocio}
                        values={values}
                        setValues={setValues}
                        vSocio={values._idSocio}
                      />
                      {values._idSocio !== '' && (
                        <>
                          <Row className='mb-2'>
                            <Col md='8'>
                              <Button block onClick={clickListarOrdenes}>
                                Mostrar Ordenes
                              </Button>
                            </Col>
                            <Col md='4'>
                              <FormGroups.FlagVerOrdenesCerradasFormGroup
                                fIsSubmitting={isSubmitting}
                                iValue={values.flagVerOrdenesCerradas}
                              />
                            </Col>
                          </Row>
                        </>
                      )}
                      <FormGroups.TituloFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.titulo}
                        iTouched={touched.titulo}
                      />
                    </Col>
                    <Col md={3}>
                      <FormGroups.TipoDocumentoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.tipoDocumento}
                        iTouched={touched.tipoDocumento}
                      />
                      <FormGroups.SerieNumeroFormGroup
                        fIsSubmitting={isSubmitting}
                        iErrorSerie={errors.serieDocumento}
                        iTouchedSerie={touched.serieDocumento}
                        iErrorNumero={errors.numeroDocumento}
                        iTouchedNumero={touched.numeroDocumento}
                        vTipoDocumento={values.tipoDocumento}
                      />
                      <FormGroups.MonedaFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.moneda}
                        iTouched={touched.moneda}
                      />
                    </Col>
                    <Col md={3}>
                      <FormGroups.FechaEmisionFormGroup
                        handleChange={handleChange}
                        values={values}
                        setValues={setValues}
                        fIsSubmitting={isSubmitting}
                        iError={errors.fechaEmision}
                        iTouched={touched.fechaEmision}
                      />
                      <FormGroups.FechaAlmacenamientoFormGroup
                        fIsSubmitting={true}
                        iError={errors.fechaAlmacenamiento}
                        iTouched={touched.fechaAlmacenamiento}
                      />
                    </Col>
                    <Col md={2}>
                      <FormGroups.TipoCambioFormGroup
                        iError={errors.tipoCambio}
                        iTouched={touched.tipoCambio}
                        fIsSubmitting={isSubmitting}
                        iMoneda={values.moneda}
                        iFechaDocumento={values.fechaEmision}
                        setFieldValue={setFieldValue}
                      />
                      {__adminContext.valoresConfiguracionCompania.flagContabilidad && (
                        <FormGroups.ModelosCuentas12FormGroup
                          fIsSubmitting={isSubmitting}
                          iError={errors._idModeloCuentas12}
                          iTouched={touched._idModeloCuentas12}
                          setFieldValue={setFieldValue}
                          vCodigoTransaccion={values.codigoTransaccion}
                        />
                      )}
                    </Col>
                  </Row>
                  <Divider />
                  <Sections.DetallesSection
                    detalles={detalles}
                    dispatch={detallesDispatcher}
                    onAbrirSeleccionUbicacion={abrirSeleccionUbicacion}
                    onAbrirRegistroLotes={abrirRegistroLotes}
                    onAbrirRegistroActivosFijos={abrirRegistroActivosFijos}
                  />
                  <Divider />
                  <Row className='mt-2'>
                    <Col md={5}>
                      <FormGroups.ComentarioFormGroup
                        iError={errors.comentario}
                        iTouched={touched.comentario}
                        fIsSubmitting={isSubmitting}
                      />
                    </Col>
                  </Row>
                  <Divider />
                  <Row>
                    <Col md={12} className='text-right'>
                      <RetrocederButton />
                      <Button disabled={isSubmitting} className='ml-2' onClick={handleSubmit}>
                        Continuar
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </>
            )}
          </Formik>
        </Col>
      </Row>
      {mostrarListaOrdenesModal && (
        <Modals.ListaOrdenesModal
          handleClose={e => setMostrarListaOrdenesModal(false)}
          show={mostrarListaOrdenesModal}
          idTemporalOrdenCompraRef={idTemporalOrdenCompraRef}
          idSocio={mainFormikRef.current.values._idSocio}
          flagVerOrdenesCerradas={mainFormikRef.current.values.flagVerOrdenesCerradas}
          toggleInfoOrdenModal={() => setMostrarInfoOrdenModal(true)}
        />
      )}
      {mostrarInfoOrdenModal && (
        <Modals.InfoOrdenModal
          handleClose={e => setMostrarInfoOrdenModal(false)}
          show={mostrarInfoOrdenModal}
          flagVerOrdenesCerradas={mainFormikRef.current.values.flagVerOrdenesCerradas}
          idOrdenCompra={idTemporalOrdenCompraRef.current}
          setValues={mainFormikRef.current.setValues}
          values={mainFormikRef.current.values}
          dispatch={detallesDispatcher}
          toggleListaOrdenesModal={() => setMostrarListaOrdenesModal(true)}
        />
      )}
      {filaUbicacion && (
        <Modals.SeleccionUbicacionModal
          show={true}
          handleClose={() => setFilaUbicacion(null)}
          onSeleccionado={onUbicacionElegida}
          valorActual={
            filaUbicacion._idUbicacion
              ? {
                  _idAlmacen: filaUbicacion._idAlmacen,
                  _idEstanteria: filaUbicacion._idEstanteria,
                  _idUbicacion: filaUbicacion._idUbicacion
                }
              : null
          }
          nombreProducto={filaUbicacion.nombreProducto || filaUbicacion.codigoProducto}
          cantidad={filaUbicacion.cantidad}
        />
      )}
      {filaRegistroLotes && (
        <Modals.RegistroLotesModal
          show={true}
          handleClose={() => setFilaRegistroLotes(null)}
          filaProducto={filaRegistroLotes}
          onGuardar={onLotesGuardados}
        />
      )}
      {filaRegistroActivosFijos && (
        <Modals.RegistroActivosFijosModal
          show={true}
          handleClose={() => setFilaRegistroActivosFijos(null)}
          filaProducto={filaRegistroActivosFijos}
          onGuardar={onUnidadesGuardadas}
        />
      )}
    </>
  )
}

export default NuevaMainForm
